import React, { useEffect, useState } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { HorizontalProgressBar } from '@im/ui-lib/HorizontalProgressBar';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import IconButton from '@mui/material/IconButton';
import { formatFileSize } from 'src/util';
import { useDiscountUpload } from '../../hooks/discountUpload';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import { useAccount } from '@azure/msal-react';

export default function UploadDetails(prop: any) {
  const file = prop?.file;
  const userName = useAccount()?.username ?? '';
  const [progress, setProgress] = useState<number>(0);
  const discountUpload = useDiscountUpload(setProgress);
  const handleTryAgain = () => {
    discountUpload.uploadApi.reset();
    const uploadReq = {
      createdBy: userName,
      file,
      modifiedBy: userName,
      programId: prop?.rowData.programId,
      programName: prop?.rowData.programName,
      vendorId: prop?.vendorId,
      vendorName: prop?.vendorName,
    };
    discountUpload.uploadApi.mutate(uploadReq);
  };
  useEffect(() => {
    handleTryAgain();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleDelete = () => {
    discountUpload.xhr?.abort();
  };
  const statusCode = discountUpload.uploadApi.data?.statusCode;
  const isError =
    !(statusCode === null || statusCode === undefined || statusCode <= 300) ||
    discountUpload.uploadApi.isError;
  useEffect(() => {
    if (
      !discountUpload.uploadApi.isLoading &&
      statusCode !== undefined &&
      statusCode !== null &&
      statusCode <= 300
    ) {
      prop.onUploadSuccess?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountUpload.uploadApi.isLoading, statusCode]);

  return (
    <Stack direction="row" justifyContent="center" spacing={0.5}>
      <Stack data-testid="gds-vp-file-icon">
        {!isError ? (
          <DescriptionOutlinedIcon
            sx={(theme) => ({ color: theme.palette.grey[600] })}
          />
        ) : (
          <WarningAmberIcon
            sx={(theme) => ({ color: theme.palette.error[700] })}
          />
        )}
      </Stack>

      <Stack direction="column" spacing={0.5} sx={{ width: '100%' }}>
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
        >
          <Typography
            sx={(theme) => ({
              color: !isError
                ? 'inherit'
                : `${theme.palette.error[700]} !important`,
              fontSize: theme.typography.body2.fontSize,
            })}
          >
            {!isError
              ? file.name
              : discountUpload.uploadApi.data?.message ??
                'Upload is cancelled successfully'}
          </Typography>

          {progress < 100 && !isError ? (
            <IconButton
              data-testid="gds-vp-delete"
              onClick={handleDelete}
              sx={{
                backgroundColor: 'transparent !important',
                boxShadow: 'none !important',
                padding: '0',
              }}
            >
              <DeleteOutlineIcon
                sx={(theme) => ({ color: theme.palette.grey[600] })}
              />
            </IconButton>
          ) : (
            <IconButton
              data-testid="gds-vp-tryagain"
              onClick={handleTryAgain}
              sx={{
                backgroundColor: 'transparent !important',
                boxShadow: 'none !important',
                padding: '0',
                pointerEvents: !isError ? 'none' : 'auto',
              }}
            >
              {!isError ? (
                !discountUpload.uploadApi.isLoading && (
                  <CheckCircleOutline
                    sx={(theme) => ({ color: theme.palette.success[700] })}
                  />
                )
              ) : (
                <RefreshIcon
                  sx={(theme) => ({
                    color: theme.palette.error[700],
                  })}
                />
              )}
            </IconButton>
          )}
        </Stack>
        <Typography
          sx={(theme) => ({
            color: !isError
              ? 'inherit'
              : `${theme.palette.error[700]} !important`,
            fontSize: theme.typography.pxToRem(10),
            fontWeight: theme.typography.fontWeightSemiBold,
            lineHeight: theme.typography.pxToRem(14),
            marginTop: '0px',
          })}
        >
          {!isError ? formatFileSize(file.size) : file.name}
        </Typography>
        {!isError && <HorizontalProgressBar value={progress} />}
      </Stack>
    </Stack>
  );
}
